import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Badge,
  Icon,
  Divider,
  Button,
  Collapse,
  useDisclosure,
  Progress,
} from '@chakra-ui/react';
import {
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronUp,
  FaHardHat,
  FaHome,
} from 'react-icons/fa';
import { NavbarWithCallToAction } from '../../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { SEOHead } from '../../../../components/SEOHead';
import { BuildStageTracker } from './components/BuildStageTracker';
import { BuildPhotoGallery } from './components/BuildPhotoGallery';
import { PhaseTaskTimeline } from './components/PhaseTaskTimeline';
import { ScheduledCallCard } from './components/ScheduledCallCard';
import { SMSFBadge } from './components/SMSFBadge';
import {
  STUB_DEAL,
  STUB_PHASE3_TASKS,
  STUB_PHASE3_PHOTOS,
  STUB_PHASE3_CALLS,
} from './stubs';
import type { PortalDeal, BuildPhoto, BuildStage } from './types';
import { BUILD_STAGE_LABELS, BUILD_STAGE_ORDER } from './types';

const PROPERTY_PRIMARY = '#0D7F6E';
const NAVY = '#141C49';

// Completed stages = all stages before current in the ordered list
const completedStages = (current: BuildStage): BuildStage[] => {
  const idx = BUILD_STAGE_ORDER.indexOf(current);
  return BUILD_STAGE_ORDER.slice(0, idx);
};

const calcProgress = (tasks: typeof STUB_PHASE3_TASKS) => {
  const total = tasks.length;
  const done = tasks.filter(t => t.status === 'COMPLETE').length;
  return total === 0 ? 0 : Math.round((done / total) * 100);
};

const PortalPhase3Construction: React.FC = () => {
  const [deal] = useState<PortalDeal>({ ...STUB_DEAL, activePhase: 3 });
  const [tasks] = useState(STUB_PHASE3_TASKS);
  const [photos, setPhotos] = useState<BuildPhoto[]>(STUB_PHASE3_PHOTOS);
  const [calls] = useState(STUB_PHASE3_CALLS);
  const { isOpen: timelineOpen, onToggle: toggleTimeline } = useDisclosure();

  const currentStage = deal.buildStage ?? 'SLAB';
  const progress = calcProgress(tasks);
  const currentStageLabel = BUILD_STAGE_LABELS[currentStage];
  const pc = deal.assignedProjectCoordinator;

  const handlePhotoUpload = (stage: BuildStage, fileUrl: string, caption: string) => {
    const newPhoto: BuildPhoto = {
      id: `ph-new-${Date.now()}`,
      stage,
      caption,
      fileUrl,
      thumbnailUrl: fileUrl,
      takenAt: new Date().toISOString(),
    };
    setPhotos(prev => [newPhoto, ...prev]);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/property/portal/phase3"
        fallbackTitle="Phase 3 — Construction | One Property Australia"
        fallbackDescription="Track your build progress stage by stage with One Property Australia"
      />
      <NavbarWithCallToAction />

      {/* Hero strip */}
      <Box bg={NAVY} color="white" py={{ base: 10, md: 14 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} align="flex-start">
            <HStack spacing={3} flexWrap="wrap">
              <Badge
                bg={PROPERTY_PRIMARY}
                color="white"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                Phase 3 of 5
              </Badge>
              <Badge
                bg="whiteAlpha.200"
                color="white"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
              >
                <HStack spacing={1}>
                  <Icon as={FaHardHat} boxSize={3} />
                  <span>Now: {currentStageLabel}</span>
                </HStack>
              </Badge>
              <SMSFBadge show={deal.isSMSF} />
            </HStack>
            <Heading size={{ base: 'xl', md: '2xl' }} fontWeight="black">
              Construction
            </Heading>
            {deal.propertyAddress && (
              <HStack spacing={2} color="gray.300">
                <Icon as={FaHome} boxSize={4} />
                <Text fontSize="md">{deal.propertyAddress}</Text>
              </HStack>
            )}
            <Text color="gray.300" maxW="xl" fontSize="md">
              Your build is progressing through 7 stages. Track every milestone, view progress
              photos, and stay connected with your Project Coordinator below.
            </Text>

            {/* Progress bar */}
            <Box w="full" maxW="md" mt={2}>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color="gray.300">Build progress</Text>
                <Text fontSize="sm" fontWeight="bold" color="white">{progress}%</Text>
              </HStack>
              <Progress
                value={progress}
                size="md"
                borderRadius="full"
                bg="whiteAlpha.200"
                sx={{ '& > div': { background: PROPERTY_PRIMARY } }}
              />
            </Box>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={{ base: 8, md: 12 }}>
        {/* Build stage tracker */}
        <Box
          bg="white"
          borderRadius="2xl"
          p={{ base: 4, md: 6 }}
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.100"
          mb={10}
        >
          <Heading size="md" color={NAVY} mb={6}>
            Build Stages
          </Heading>
          <BuildStageTracker currentStage={currentStage} />
        </Box>

        {/* Photos + right sidebar */}
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} mb={10}>
          {/* Left: photos + recent updates */}
          <Box gridColumn={{ lg: '1 / 3' }}>
            <VStack spacing={8} align="stretch">
              {/* Photo gallery */}
              <Box>
                <Heading size="md" color={NAVY} mb={5}>
                  Build Photos
                </Heading>
                <Box
                  bg="white"
                  borderRadius="2xl"
                  p={{ base: 4, md: 6 }}
                  boxShadow="sm"
                  border="1px solid"
                  borderColor="gray.100"
                >
                  <BuildPhotoGallery
                    photos={photos}
                    currentUserIsPC={false}
                    onUpload={handlePhotoUpload}
                  />
                </Box>
              </Box>

              {/* Recent updates / timeline (collapsible) */}
              <Box>
                <HStack
                  justify="space-between"
                  cursor="pointer"
                  onClick={toggleTimeline}
                  mb={timelineOpen ? 4 : 0}
                >
                  <Heading size="md" color={NAVY}>
                    Build Timeline
                  </Heading>
                  <Icon
                    as={timelineOpen ? FaChevronUp : FaChevronDown}
                    color="gray.400"
                  />
                </HStack>
                <Collapse in={timelineOpen}>
                  <Box
                    bg="white"
                    borderRadius="2xl"
                    p={{ base: 4, md: 6 }}
                    boxShadow="sm"
                    border="1px solid"
                    borderColor="gray.100"
                  >
                    <PhaseTaskTimeline tasks={tasks} />
                  </Box>
                </Collapse>
                {!timelineOpen && (
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Click to expand your full build milestone list
                  </Text>
                )}
              </Box>
            </VStack>
          </Box>

          {/* Right sidebar: PC card + calls */}
          <Box>
            <VStack spacing={6} align="stretch">
              {/* Project Coordinator card */}
              {pc && (
                <Box>
                  <Heading size="md" color={NAVY} mb={4}>
                    Your Project Coordinator
                  </Heading>
                  <Box
                    bg="white"
                    borderRadius="2xl"
                    p={5}
                    boxShadow="sm"
                    border="1px solid"
                    borderColor="gray.100"
                    position="relative"
                    overflow="hidden"
                    _before={{
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      w: '4px',
                      h: '100%',
                      bg: PROPERTY_PRIMARY,
                      borderRadius: '4px 0 0 4px',
                    }}
                  >
                    <VStack align="flex-start" spacing={3}>
                      <VStack align="flex-start" spacing={0}>
                        <Text fontWeight="bold" color={NAVY}>{pc.name}</Text>
                        <Text fontSize="sm" color="gray.500">{pc.role}</Text>
                      </VStack>
                      <VStack align="flex-start" spacing={1.5}>
                        {pc.email && (
                          <HStack spacing={2} color="gray.500">
                            <Icon as={FaEnvelope} boxSize={3.5} />
                            <Text fontSize="xs">{pc.email}</Text>
                          </HStack>
                        )}
                        {pc.phone && (
                          <HStack spacing={2} color="gray.500">
                            <Icon as={FaPhone} boxSize={3.5} />
                            <Text fontSize="xs">{pc.phone}</Text>
                          </HStack>
                        )}
                      </VStack>
                      <Text fontSize="xs" color="gray.500" lineHeight="1.5">
                        {pc.name.split(' ')[0]} will keep you updated at every stage and is your
                        direct point of contact throughout construction.
                      </Text>
                      {pc.bookingUrl && (
                        <Button
                          as="a"
                          href={pc.bookingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="sm"
                          bg={PROPERTY_PRIMARY}
                          color="white"
                          _hover={{ bg: '#0a6b5d' }}
                          leftIcon={<FaCalendarAlt />}
                          w="full"
                          borderRadius="lg"
                        >
                          Book a Call
                        </Button>
                      )}
                    </VStack>
                  </Box>
                </Box>
              )}

              {/* Upcoming calls */}
              <Box>
                <Heading size="md" color={NAVY} mb={4}>
                  Scheduled Calls
                </Heading>
                {calls.length === 0 ? (
                  <Box
                    bg="white"
                    borderRadius="xl"
                    p={5}
                    textAlign="center"
                    border="1px dashed"
                    borderColor="gray.300"
                  >
                    <Text color="gray.400" fontSize="sm">
                      No calls scheduled — {pc?.name?.split(' ')[0] ?? 'your coordinator'} will
                      be in touch for your fortnightly check-in
                    </Text>
                  </Box>
                ) : (
                  <VStack spacing={3} align="stretch">
                    {calls.map(call => (
                      <ScheduledCallCard key={call.id} call={call} />
                    ))}
                  </VStack>
                )}
              </Box>

              {/* Fortnightly cadence note */}
              <Box
                bg="teal.50"
                border="1px solid"
                borderColor="teal.200"
                borderRadius="xl"
                p={4}
              >
                <HStack spacing={2} mb={2}>
                  <Icon as={FaCalendarAlt} color={PROPERTY_PRIMARY} boxSize={4} />
                  <Text fontSize="sm" fontWeight="semibold" color="teal.800">
                    Fortnightly updates
                  </Text>
                </HStack>
                <Text fontSize="xs" color="teal.700" lineHeight="1.6">
                  You'll receive a progress update from {pc?.name?.split(' ')[0] ?? 'your coordinator'} every
                  two weeks throughout your build. Questions? Reply to any update SMS or book a
                  call above.
                </Text>
              </Box>
            </VStack>
          </Box>
        </SimpleGrid>

        <Divider mb={8} />

        {/* Cross-division solar callout (triggered at land settlement per spec) */}
        <Box
          bgGradient="linear(135deg, teal.50, cyan.50)"
          border="1px solid"
          borderColor="teal.200"
          borderRadius="2xl"
          p={6}
        >
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="center">
            <VStack align="flex-start" spacing={2}>
              <Badge colorScheme="teal" borderRadius="full" fontSize="xs">
                One Solar Australia
              </Badge>
              <Heading size="sm" color={NAVY}>
                Maximise your investment with solar
              </Heading>
              <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                Installing solar during construction increases rental appeal, reduces tenant
                energy costs, and can be included in your depreciation schedule from day one.
              </Text>
            </VStack>
            <VStack align={{ base: 'flex-start', md: 'flex-end' }}>
              <Button
                as="a"
                href="/solar"
                size="md"
                bg={PROPERTY_PRIMARY}
                color="white"
                _hover={{ bg: '#0a6b5d' }}
                borderRadius="lg"
              >
                Explore Solar Options
              </Button>
              <Text fontSize="xs" color="gray.500">
                Speak to your strategist if you'd like to discuss
              </Text>
            </VStack>
          </SimpleGrid>
        </Box>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PortalPhase3Construction;
